import { z } from 'zod';

import { planFeaturesOverrideSchema, type PlanFeaturesOverride } from './billing/plans';

/**
 * Runtime config registry — the single source of truth for the *tunable* domain
 * rules (the "business rules that actually change"). Each entry pairs a stable
 * key with its group, value schema, compiled-in default and the BR(s) it backs.
 *
 * The admin panel edits these live (stored in Cloudflare KV); the worker reads
 * `override ?? default` at runtime via `getConfig`. Invariants/constraints are
 * NOT here — they stay enforced in code. A missing/malformed override always
 * falls back to the default, so config can never break correctness.
 */

/** Value type discriminator for how the admin UI renders an editor. */
export type ConfigValueType = 'number' | 'boolean' | 'json';

export interface ConfigEntry<T> {
  /** Grouping for the admin screen (Scoring · Session · Cost · Flags …). */
  group: string;
  type: ConfigValueType;
  /** Compiled-in default (equals today's hardcoded constant → no behaviour change). */
  default: T;
  /** Zod schema the stored override must satisfy (else it's ignored → default). */
  schema: z.ZodType<T>;
  /** Human description shown in the admin editor. */
  description: string;
  /** BR ids this key backs (reverse link into the `business_rules` catalog). */
  br: string[];
  /** Optional numeric bounds for the UI (number type only). */
  min?: number;
  max?: number;
  /**
   * Whole numbers only (number type). Declared rather than introspected out of
   * the zod schema, and kept honest by a test that checks every entry's schema
   * agrees — an editor offering 0.05 steps on a knob the API rejects unless it
   * is an integer invites you to type a value that cannot be saved.
   */
  integer?: true;
}

/** total_score = mastery·wMastery + explanation·wExplanation + (100−aiDep)·wAiDependency. */
export const totalWeightsSchema = z
  .object({
    mastery: z.number().min(0).max(1),
    explanation: z.number().min(0).max(1),
    aiDependency: z.number().min(0).max(1),
  })
  .strict();
export type TotalWeights = z.infer<typeof totalWeightsSchema>;

/** Tunable heuristic-scoring policy thresholds (BR-8/9/14). Formula internals
 * (point values) stay in code — these are the operator-facing bands. */
export const scoringHeuristicSchema = z
  .object({
    /** BR-8: min characters before an answer is flagged "too short". */
    minAnswerLength: z.number().int().min(1).max(500),
    /** BR-9: below this many ms a non-trivial answer looks copy/paste-fast. */
    tooFastMs: z.number().int().min(0).max(120_000),
    /** BR-14: strength is credited at/above these scores. */
    strengthMasteryMin: z.number().min(0).max(100),
    strengthExplanationMin: z.number().min(0).max(100),
    strengthAiDependencyMax: z.number().min(0).max(100),
    /** BR-14: weakness is flagged below (mastery/explanation) / above (aiDep) these. */
    weaknessMasteryMax: z.number().min(0).max(100),
    weaknessExplanationMax: z.number().min(0).max(100),
    weaknessAiDependencyMin: z.number().min(0).max(100),
  })
  .strict();
export type ScoringHeuristicConfig = z.infer<typeof scoringHeuristicSchema>;

/** Roadmap targeting thresholds (PRE-79/150/157). Decide which themes fire and
 * which skills count as weak — pure policy, admin-tunable. */
export const roadmapThresholdsSchema = z
  .object({
    /** aiDependency strictly above → independence theme. */
    aiDependency: z.number().min(0).max(100),
    /** explanation strictly below → explanation theme. */
    explanation: z.number().min(0).max(100),
    /** debugging strictly below → debugging theme. */
    debugging: z.number().min(0).max(100),
    /** skill average below → weak skill. */
    weakSkill: z.number().min(0).max(100),
    /** PRE-79: a single question mastery below this hard floor marks its skill weak. */
    weakConceptFloor: z.number().min(0).max(100),
    /** PRE-157: synthetic score for a self-declared weak skill (< weakSkill). */
    selfDeclaredWeakScore: z.number().min(0).max(100),
  })
  .strict();
export type RoadmapThresholds = z.infer<typeof roadmapThresholdsSchema>;

/** Habit-nudge push copy (BR-80 / PRE-148). Admin-editable EN heading + body. */
export const habitNudgeCopySchema = z
  .object({
    active: z
      .object({
        heading: z.string().trim().min(1).max(100),
        body: z.string().trim().min(1).max(500),
      })
      .strict(),
    reassess: z
      .object({
        heading: z.string().trim().min(1).max(100),
        body: z.string().trim().min(1).max(500),
      })
      .strict(),
  })
  .strict();
export type HabitNudgeCopy = z.infer<typeof habitNudgeCopySchema>;

export const DEFAULT_HABIT_NUDGE_COPY: HabitNudgeCopy = {
  active: {
    heading: 'Your practice is waiting 🎯✨',
    body: 'You still have an unfinished step on your roadmap. Jump back in and keep the streak going! 💪',
  },
  reassess: {
    heading: 'You finished your roadmap 🎉🚀',
    body: 'Ready to see how far you have come? Reassess and unlock your next plan. 🔥',
  },
};

/**
 * The registry. Keys are dot-namespaced (`group.name`). Add an entry here when
 * a tunable rule should become admin-editable; replace its hardcoded constant
 * with `getConfig(env, key)` whose fallback is this `default`.
 */
export const CONFIG_REGISTRY = {
  'scoring.totalWeights': {
    group: 'Scoring',
    type: 'json',
    default: { mastery: 0.4, explanation: 0.35, aiDependency: 0.25 },
    schema: totalWeightsSchema,
    description:
      'BR-13 total_score weights: mastery·0.4 + explanation·0.35 + (100−aiDependency)·0.25. Should sum to ~1.',
    br: ['BR-13'],
  } satisfies ConfigEntry<TotalWeights>,

  'scoring.blend.llmWeight': {
    group: 'Scoring',
    type: 'number',
    default: 0.6,
    schema: z.number().min(0).max(1),
    description:
      'BR-7 rubric-judge blend: weight of the LLM overall score (heuristic gets 1−this). 0.6 by default.',
    br: ['BR-7'],
    min: 0,
    max: 1,
  } satisfies ConfigEntry<number>,

  'scoring.heuristic': {
    group: 'Scoring',
    type: 'json',
    default: {
      minAnswerLength: 20,
      tooFastMs: 10_000,
      strengthMasteryMin: 60,
      strengthExplanationMin: 60,
      strengthAiDependencyMax: 30,
      weaknessMasteryMax: 50,
      weaknessExplanationMax: 50,
      weaknessAiDependencyMin: 50,
    },
    schema: scoringHeuristicSchema,
    description:
      'BR-8/9/14 heuristic-scoring policy: min answer length, too-fast ms, and the strength/weakness score bands. Scoring formula internals (point values) stay in code.',
    br: ['BR-8', 'BR-9', 'BR-14'],
  } satisfies ConfigEntry<ScoringHeuristicConfig>,

  'roadmap.thresholds': {
    group: 'Roadmap',
    type: 'json',
    default: {
      aiDependency: 70,
      explanation: 60,
      debugging: 60,
      weakSkill: 60,
      weakConceptFloor: 40,
      selfDeclaredWeakScore: 55,
    },
    schema: roadmapThresholdsSchema,
    description:
      'PRE-79/150/157 roadmap targeting: the score thresholds that decide which focus themes fire (independence/explanation/debugging) and which skills count as weak.',
    br: ['BR-37'],
  } satisfies ConfigEntry<RoadmapThresholds>,

  'content.coverageFloor': {
    group: 'Content',
    type: 'number',
    default: 5,
    schema: z.number().int().min(1).max(50),
    description:
      'PRE-168/286 coverage floor: published questions a (skill × difficulty) cell needs when NOBODY has claimed the skill. The target grows from here with demand — see content.coverageDemandPerQuestion.',
    br: ['PRE-168', 'PRE-286'],
    min: 1,
    integer: true,
    max: 50,
  } satisfies ConfigEntry<number>,

  'content.coverageTargetMax': {
    group: 'Content',
    type: 'number',
    default: 12,
    schema: z.number().int().min(1).max(50),
    description:
      'PRE-286: ceiling on a cell target, however wanted the skill is. A stop rather than a dial — human review is the bottleneck, and an uncapped target fills the worklist with depth nobody reaches.',
    br: ['PRE-286'],
    min: 1,
    integer: true,
    max: 50,
  } satisfies ConfigEntry<number>,

  'content.coverageDemandPerQuestion': {
    group: 'Content',
    type: 'number',
    default: 3,
    schema: z.number().int().min(1).max(100),
    description:
      'PRE-286: units of demand (learners claiming the skill + roadmaps targeting it) that buy one more question in each of its cells. A standing ASSUMPTION about exposure, not a measurement — PRE-260/261 replace it once the bank has enough answers to say how many learners one item serves.',
    br: ['PRE-286'],
    min: 1,
    integer: true,
    max: 100,
  } satisfies ConfigEntry<number>,

  'dedup.cosineThreshold': {
    group: 'Content',
    type: 'number',
    default: 0.9,
    schema: z.number().min(0).max(1),
    description:
      'PRE-169 near-duplicate sensitivity: cosine similarity at/above which a generated draft is flagged as a semantic duplicate for the reviewer. Advisory — never auto-rejects. Lower = stricter (more flags).',
    br: ['BR-83'],
    min: 0,
    max: 1,
  } satisfies ConfigEntry<number>,

  'authoring.judgeRejectBelow': {
    group: 'Content',
    type: 'number',
    default: 0.34,
    schema: z.number().min(0).max(1),
    description:
      'PRE-262: judge score below which a generated draft is rejected before any human sees it. Human review is the bottleneck at ~15,000 cells, so the cheapest reviewer time is the time never spent. The default 0.34 blocks only drafts a MAJORITY of best-of-3 judges failed — far more conservative than the 0.6 "likely approve" line, because a wrong threshold silently discards good questions. Blocked drafts are stored as rejected with reason `judge_blocked`, never deleted, so the threshold can be judged after the fact. Set to 0 to disable blocking and return the judge to purely advisory.',
    br: ['BR-84'],
    min: 0,
    max: 1,
  } satisfies ConfigEntry<number>,

  'authoring.judgeVotes': {
    group: 'Content',
    type: 'number',
    default: 3,
    schema: z.number().int().min(1).max(9),
    description:
      'PRE-171 advisory authoring judge: number of best-of-N LLM votes per generated draft; the pass fraction becomes the 0..1 quality score that sorts the review queue. Advisory — never auto-approves.',
    br: ['BR-84'],
    min: 1,
    integer: true,
    max: 9,
  } satisfies ConfigEntry<number>,

  'session.dailyGoalSessions': {
    group: 'Session',
    type: 'number',
    default: 1,
    schema: z.number().int().min(1).max(10),
    description:
      'BR-75 soft daily goal: number of practice sessions a user is expected to complete per day (habit anchor shown on the Progress screen). Never blocks — purely motivational.',
    br: ['BR-75'],
    min: 1,
    integer: true,
    max: 10,
  } satisfies ConfigEntry<number>,

  'cv.skillRateTopN': {
    group: 'Content',
    type: 'number',
    default: 5,
    schema: z.number().int().min(0).max(40),
    description:
      "PRE-176 CV rating breadth: how many of a CV's skills get an evidence rating. The call runs 17-38s and grows with the list, and it is the last thing standing between an upload and a complete review screen — so this trades how many cards carry a hint against how long every card waits for one. Rated in the order the CV named them. 0 rates every skill (the old behaviour).",
    br: ['PRE-176'],
    min: 0,
    integer: true,
    max: 40,
  } satisfies ConfigEntry<number>,

  'llm.generationMonthlyCapUsd': {
    group: 'Cost',
    type: 'number',
    default: 50,
    schema: z.number().min(0).max(10_000),
    description:
      'PRE-173 authoring soft cap: monthly USD spend on question generation that flips an alert flag in the admin Costs view. Soft only — never blocks generation. 0 disables the alert.',
    br: ['PRE-173'],
    min: 0,
    max: 10_000,
  } satisfies ConfigEntry<number>,

  'session.timeoutMin': {
    group: 'Session',
    type: 'number',
    default: 30,
    schema: z.number().int().min(1).max(1440),
    description:
      'BR-49 idle soft-timeout (minutes): an IN_PROGRESS session idle longer than this auto-pauses on next access. Never loses work — it just parks the session.',
    br: ['BR-49'],
    min: 1,
    integer: true,
    max: 1440,
  } satisfies ConfigEntry<number>,

  'gdpr.purgeGraceDays': {
    group: 'Users',
    type: 'number',
    default: 7,
    schema: z.number().int().min(0).max(365),
    description:
      'BR-65 GDPR purge grace (days): how long after a deletion request the daily purge waits before hard-deleting the account. 0 purges on the next run.',
    br: ['BR-65'],
    min: 0,
    integer: true,
    max: 365,
  } satisfies ConfigEntry<number>,

  'admin.resetAllowedEmails': {
    group: 'Users',
    type: 'json',
    default: [] as string[],
    schema: z.array(z.string()),
    description:
      'BR-85 reset allowlist: ONLY these emails may be data-reset via the admin Users screen (case-insensitive, exact match). Empty = nobody can be reset — a safe default so no real account is ever touched until explicitly allowlisted.',
    br: ['BR-85'],
  } satisfies ConfigEntry<string[]>,

  'freeTier.dailySessions': {
    group: 'Cost',
    type: 'number',
    default: 5,
    schema: z.number().int().min(0).max(1000),
    description:
      'BR-36 free-tier daily practice-session ceiling (per user, UTC day). 0 disables the limit.',
    br: ['BR-36'],
    min: 0,
    integer: true,
    max: 1000,
  } satisfies ConfigEntry<number>,

  'freeTier.dailyHints': {
    group: 'Cost',
    type: 'number',
    default: 15,
    schema: z.number().int().min(0).max(1000),
    description: 'BR-36 free-tier daily hint ceiling (per user, UTC day). 0 disables the limit.',
    br: ['BR-36'],
    min: 0,
    integer: true,
    max: 1000,
  } satisfies ConfigEntry<number>,

  'billing.planFeatures': {
    group: 'Cost',
    type: 'json',
    default: {
      free: { daily_sessions: 5, daily_hints: 15 },
      pro: { daily_sessions: 'unlimited' as const, daily_hints: 'unlimited' as const },
    },
    schema: planFeaturesOverrideSchema,
    description:
      'BR-86 plan×feature limit matrix (PRE-247). Sparse overrides allowed; omitted keys use compiled defaults. `unlimited` or 0 disables a daily cap. Prefer this over freeTier.* for new plans/features.',
    br: ['BR-86', 'BR-36'],
  } satisfies ConfigEntry<PlanFeaturesOverride>,

  'flags.emailPasswordAuth': {
    group: 'Flags',
    type: 'boolean',
    // `as boolean` so ConfigValue widens from the `false` literal → callers can
    // pass a runtime boolean fallback (env-var value) to getConfig.
    default: false as boolean,
    schema: z.boolean(),
    description:
      'BR-26 email/password login. OFF by default (social only). Enabling it never grants admin — the ADMIN_EMAILS allowlist still gates /v1/admin/*.',
    br: ['BR-26'],
  } satisfies ConfigEntry<boolean>,

  'habitNudge.hourUtc': {
    group: 'Push',
    type: 'number',
    default: 17,
    schema: z.number().int().min(0).max(23),
    description:
      'BR-80 habit-nudge send hour (UTC). Workers cron runs hourly (`0 * * * *`); the batch only sends when the current UTC hour matches this value. Default 17 (= 17:00 UTC).',
    br: ['BR-80'],
    min: 0,
    integer: true,
    max: 23,
  } satisfies ConfigEntry<number>,

  'habitNudge.activeEnabled': {
    group: 'Push',
    type: 'boolean',
    default: true as boolean,
    schema: z.boolean(),
    description:
      'BR-80: when ON, send the active-roadmap "continue today" push (`roadmap_today`) to eligible users.',
    br: ['BR-80'],
  } satisfies ConfigEntry<boolean>,

  'habitNudge.reassessEnabled': {
    group: 'Push',
    type: 'boolean',
    default: true as boolean,
    schema: z.boolean(),
    description: 'BR-80: when ON, send the completed-roadmap reassess push to eligible users.',
    br: ['BR-80'],
  } satisfies ConfigEntry<boolean>,

  'habitNudge.copy': {
    group: 'Push',
    type: 'json',
    default: DEFAULT_HABIT_NUDGE_COPY,
    schema: habitNudgeCopySchema,
    description:
      'BR-80 habit-nudge push heading/body (English). `active` = roadmap_today; `reassess` = completed roadmap. Deep-links stay fixed in code.',
    br: ['BR-80'],
  } satisfies ConfigEntry<HabitNudgeCopy>,

  'assessment.totalQuestions': {
    group: 'Assessment',
    type: 'number',
    default: 9,
    schema: z.number().int().min(5).max(20),
    description:
      'BR-87: how many questions one assessment carries. The 9 Ağustos decision was ~8-10; the range keeps it long enough to measure more than one skill and short enough to finish in a sitting.',
    br: ['BR-87'],
    min: 5,
    integer: true,
    max: 20,
  } satisfies ConfigEntry<number>,

  'assessment.discoveryRatio': {
    group: 'Assessment',
    type: 'number',
    default: 0.3,
    schema: z.number().min(0).max(1),
    description:
      'BR-87: the share of an assessment spent on skills the learner never claimed. Rounded down, so 9 questions spend 2 — the claim is still the better prior and discovery is the hedge against it. 0 makes an assessment able to confirm what the learner already suspected and nothing else.',
    br: ['BR-87'],
    min: 0,
    max: 1,
  } satisfies ConfigEntry<number>,
} as const;

export type ConfigKey = keyof typeof CONFIG_REGISTRY;

/** Value type for a given key. */
export type ConfigValue<K extends ConfigKey> = (typeof CONFIG_REGISTRY)[K]['default'];

/** All keys (stable order for the admin screen). */
export const CONFIG_KEYS = Object.keys(CONFIG_REGISTRY) as ConfigKey[];

/** Runtime guard: is a string a known config key? */
export function isConfigKey(k: string): k is ConfigKey {
  return Object.prototype.hasOwnProperty.call(CONFIG_REGISTRY, k);
}

/** Shape returned by the admin GET /config endpoint per key. */
export const adminConfigItemSchema = z.object({
  key: z.string(),
  group: z.string(),
  type: z.enum(['number', 'boolean', 'json']),
  description: z.string(),
  br: z.array(z.string()),
  /** The compiled-in default. */
  defaultValue: z.unknown(),
  /** The stored override, or null when running on the default. */
  override: z.unknown().nullable(),
  /** override ?? default — what the worker actually uses. */
  effective: z.unknown(),
  min: z.number().nullable(),
  max: z.number().nullable(),
  /** Whole numbers only — the editor steps by 1 and refuses decimals. */
  integer: z.boolean(),
});
export type AdminConfigItem = z.infer<typeof adminConfigItemSchema>;

export const adminConfigResponseSchema = z.object({ items: z.array(adminConfigItemSchema) });
export type AdminConfigResponse = z.infer<typeof adminConfigResponseSchema>;

/** PATCH /config/:key body — the new value (validated per-key on the server). */
export const updateConfigInputSchema = z.object({ value: z.unknown() });
export type UpdateConfigInput = z.infer<typeof updateConfigInputSchema>;
